// =============================================================================
// Copyright   : 2023 semantics GmbH
// Date        : $Date: 2024-11-09 13:43:56 +0100 (Sat, 09 Nov 2024) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 147382 $
// Purpose     : llm integration
// =============================================================================
// ---Third-party imports
import React from 'react'
import now from 'performance-now'
import clsx from 'clsx'
import {Button, Icon, Item, Message, List, Loader, Header, Segment} from 'semantic-ui-react'

// ---Framework imports
import log from 'uix/logging'
import {
  mapItems,
  strip,
  sum,
  callIfFunc,
  sorted,
  reversed,
  history,
  dict,
  keys,
  isString,
  isEqual,
  size,
  rstrip,
} from 'ldash'
import {Actions} from 'uix/registries'
import {Markdown, Highlight} from 'uix/chunks'
import {AutoSizer, XMenu, DelayedDisplay} from 'uix/components'
import {
  useRef,
  useResettableState,
  usePersistent,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useApi,
} from 'uix/hooks'
import {usePersistentToggle} from 'uix/components/menu'

// ---Local imports
import styles from './chat.scss'
import {usePersistentChat, useContextToggles, useDelayedText} from './hooks'
import {LUCKY} from './consts'

// ---Exports

const DEFAULT_MODEL = {name: 'gpt-4o-mini', maxInputTokens: 128000}
const ModelSelector = ({model, setModel, capability}) => {
  let {data} = useApi('lab/getActiveLLMs', {}, {cached: true})
  let models = useMemo(() => {
    let models = data?.models ?? []
    if (capability) {
      models = models.filter(m => m.capabilities[capability])
    }
    return models
  }, [data, capability])

  // switch to first if current model name is invalid
  useEffect(() => {
    if (models.filter(m => m.name == model.name).length == 0 && models.length > 0) {
      setModel(models[0])
    }
  }, [models])
  return (
    <XMenu.Dropdown
      options={models.map(m => ({
        value: m.name,
        key: m.name,
        text: m.name,
      }))}
      value={model.name}
      onValueChange={v => setModel(models.filter(m => m.name == v)[0])}
    />
  )
}
export const useModelSelector = ({capability}) => {
  let [model, setModel] = usePersistent('model', DEFAULT_MODEL)
  let modelSelector = <ModelSelector setModel={setModel} model={model} capability={capability} />
  return [model, modelSelector]
}

const estimateTokens = text => {
  /* Estimates the number of tokens in a given text.
   This function provides a rough estimate of the number of tokens
   in a text string.  It's based on word count, character count, and punctuation.
  */
  const additionalTokens = (text.match(/[\s.,!?;]/g) || []).length
  return Math.round((text.split(' ').length / 0.75 + text.length / 4 + 2 * additionalTokens) / 2)
}

const PreText = ({text}) => (
  <pre
    style={{
      margin: '0px',
      textWrap: 'balance',
      lineHeight: '1.0em',
      fontFamily: 'Arial, sans-serif',
    }}
  >
    {text}
  </pre>
)

const PauseElement = ({unpause, remainingPauseTime}) => {
  const [timeLeft, setTimeLeft] = useState(remainingPauseTime)

  useEffect(() => {
    // Update the timeLeft every 100ms
    const interval = setInterval(() => {
      setTimeLeft(prevTime => {
        const newTime = prevTime - 100
        return newTime >= 0 ? newTime : 0
      })
    }, 100)

    // Clear the interval when the component unmounts
    return () => clearInterval(interval)
  }, [remainingPauseTime])

  // Calculate the percentage for the progress bar
  const percentage = remainingPauseTime > 0 ? (timeLeft / remainingPauseTime) * 100 : 0
  return (
    <span
      onClick={unpause}
      style={{display: 'inline-flex', alignItems: 'center', cursor: 'pointer', marginLeft: '10px'}}
    >
      {Math.ceil((timeLeft || 0) / 1000)}s&nbsp;
      <div
        style={{
          position: 'relative',
          width: '60px',
          height: '6px',
          backgroundColor: '#ddd',
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: '#76c7c0',
            transition: 'width 0.1s linear',
          }}
        ></div>
      </div>
    </span>
  )
}

let resolveFunctions = (text, function_calls) => {
  if (size(function_calls)) {
    let whitelist = function_calls.map(f => f.name)
    const startDelimiter = '\n```json\n'
    const endDelimiter = '\n```\n'
    const funcNamesPattern = whitelist.join('|')

    //~ const regex = new RegExp(`(${funcNamesPattern})\\s*(\\{[^]*?\\})`, 'g')
    //~ return text.replace(regex, `${startDelimiter} $1 $2 ${endDelimiter}`)

    // Regular expression to split the text into code and non-code segments
    const splitter = /(```[\s\S]*?```|`[^`]*`)/g

    const segments = text.split(splitter)

    const processedSegments = segments.map(segment => {
      // Check if the segment is code (inline or block)
      if (/^(```[\s\S]*?```|`[^`]*`)$/.test(segment)) {
        // Return code segments unmodified
        return segment
      } else {
        // Process non-code segments
        const regex = new RegExp(`(${funcNamesPattern})\\s*(\\{[^]*?\\})`, 'g')
        return segment.replace(regex, `${startDelimiter} $1 $2 ${endDelimiter}`)
      }
    })

    text = processedSegments.join('')
  }
  return text
}

const MessageOutput = ({
  msg,
  onFunctionCallClick,
  onMarkdownLinkClick,
  pauseOnMarkdownLink,
  debug,
  wordsPerSec,
}) => {
  /* render with markdown-to-jsx
   */
  if (wordsPerSec == 0 && pauseOnMarkdownLink) {
    // pauseOnMarkdownLink needs at least some slowdown (0 will pass through full chunks)
    wordsPerSec = 6
  }
  let {text, pauses} = useDelayedText({
    text: msg.content,
    enabled: !!msg.meta?.streaming,
    wordsPerSec,
    pauseOnMarkdownLink,
  })
  if (pauses.activePause) {
    text = rstrip(text) + ' <Pause>'
  }

  useEffect(() => {
    if (pauses.activePause?.target.href) {
      callIfFunc(onMarkdownLinkClick, {href: pauses.activePause.target.href})
    }
  }, [pauses.activePause])

  let buildPause = () => (
    <PauseElement unpause={pauses.unpause} remainingPauseTime={pauses.remainingPauseTime(now())} />
  )

  let funcResolvedText = resolveFunctions(text, msg.meta?.function_calls)

  // escape unescaped periods in numbered markdown link labels
  let escapedMarkdown = useMemo(() => funcResolvedText.replace(/(\[)(\d+)\.(?!\\)/g, '$1$2\\.'), [
    funcResolvedText,
  ])

  let getFuncCallfromCode = code => {
    code = strip(code)

    for (let prefix of ['json', 'plaintext']) {
      if (code.startsWith(prefix)) {
        code = strip(code.slice(prefix.length))
      }
    }

    let fcs = msg.meta?.function_calls || []
    let fc = fcs.find(({name, arguments: args}) => {
      if (code.startsWith(name)) {
        let parsed_args = JSON.parse(code.slice(name.length))
        return isEqual(parsed_args, args)
      }
    })

    return fc
  }

  let onCodeClick = code => {
    /* for embedded function calls in messages
    match code in chat messages to collected function calls
    */
    if (isString(code)) {
      let fc = getFuncCallfromCode(code)

      if (fc) {
        callIfFunc(onFunctionCallClick, fc)
      } else {
        log.warn('did not find code in function_calls')
      }
    } else {
      log.warn('code is no raw string')
    }
  }

  let buildCode = props => {
    let onClick = undefined
    if (
      size(msg.meta?.function_calls) &&
      isString(props.children) &&
      getFuncCallfromCode(props.children)
    ) {
      onClick = onCodeClick
    }
    return <HighlightedCode onCodeClick={onClick} {...props} />
  }

  let _onMarkdownLinkClick = (event, href) => {
    if (!href?.startsWith('http')) {
      event.preventDefault() // Prevent the default redirect
      callIfFunc(onMarkdownLinkClick, {href})
    }
  }

  let buildLink = ({children, href, ...props}) => {
    if (href?.startsWith('http')) {
      props = {...props, target: '_mdlink'}
    }
    return (
      <a {...props} href={href} onClick={event => _onMarkdownLinkClick(event, href)}>
        {children}
      </a>
    )
  }

  let res = (
    <Markdown
      options={{
        overrides: {
          code: buildCode,
          a: buildLink,
          Pause: buildPause,
        },
      }}
    >
      {escapedMarkdown}
    </Markdown>
  )
  // show raw output in debug mode
  if (debug) {
    res = <PreText text={escapedMarkdown} />
  }

  return res
}

const MessageEditor = ({msg, index, onClose, onSave}) => {
  // State to hold the editable content of the message
  const [editContent, setEditContent] = useState(msg.content)

  // Create a ref for the textarea to capture focus
  const textareaRef = useRef(null)

  // Handle change in the text area
  const handleChange = e => {
    setEditContent(e.target.value)
    autoResizeTextarea(e.target)
  }

  // Automatically resize the textarea based on content
  const autoResizeTextarea = textarea => {
    textarea.style.height = 'auto' // Reset the height
    textarea.style.height = `${textarea.scrollHeight}px` // Set it to the scrollHeight
  }

  // Handle save action
  const handleSave = execute => {
    callIfFunc(onSave, index, editContent, execute)
    onClose() // Close the editor after saving
  }

  // Handle cancel action: simply close the editor without saving changes
  const handleCancel = () => {
    onClose()
  }

  // Handle key down events for handling submit and newline
  const handleTextareaKeyDown = e => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow the default behavior of adding a newline
      } else if (e.ctrlKey) {
        e.preventDefault()
        handleSave(true)
      } else {
        e.preventDefault()
        handleSave(false)
      }
    }
  }

  // Add event listener for Escape key press
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    // Add the keydown event listener when the component mounts
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup: remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose]) // Dependency array contains onClose to ensure freshness

  // Capture focus on the textarea when the component mounts
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current
      textarea.focus()
      textarea.selectionStart = textarea.selectionEnd = textarea.value.length // Move cursor to end
      autoResizeTextarea(textarea)
    }
  }, []) // Empty dependency array to run on component mount

  return (
    <div className={styles.messageEditor}>
      <textarea
        ref={textareaRef}
        value={editContent}
        onChange={handleChange}
        onKeyDown={handleTextareaKeyDown} // Add keydown handler here
        style={{
          width: '100%',
          padding: '10px',
          paddingBottom: '0px',
          fontFamily: 'Arial, sans-serif',
          border: '1px solid #ccc',
          borderRadius: '5px',
          overflow: 'hidden', // Hide scrollbar as we dynamically adjust size
          resize: 'none', // Prevent manual resize
        }}
        onBlur={onClose}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '10px',
        }}
      >
        <span style={{fontSize: '12px', color: '#666'}}>
          <strong>Enter</strong> (save) <strong>+Shift</strong> (new line) <strong>+Ctrl</strong>{' '}
          (run) <strong>Escape</strong> (close)
        </span>
        <div>
          <Button onClick={handleCancel} compact basic>
            Cancel
          </Button>
          <Button onClick={() => handleSave(false)} compact secondary>
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}

const SystemMessageItem = ({
  returnedSystemMessages,
  rawSystemMessages,
  systemMessageEditor,
  model,
}) => {
  // The markdown parser may misinterpret numbers followed by periods
  // as the start of ordered lists or nested lists, leading to incorrect HTML output.
  // To prevent this, use a regular expression to escape the periods after
  // numbers in the list items.
  let msgs = returnedSystemMessages
  if (rawSystemMessages) {
    msgs = rawSystemMessages.map(m => ({content: m}))
  }
  let escape = md => md.replace(/(-\s.*?)(\d+)\.(?!\\)/g, '$1$2\\.')

  let [sysmsgMarkdown, sysmsgMarkdown_toggle] = usePersistentToggle('sysmsgMarkdown', {
    content: 'Markdown',
    icon: 'eye',
  })

  let menu = (
    <XMenu size="tiny" borderless padded>
      {systemMessageEditor}
      {sysmsgMarkdown_toggle}
    </XMenu>
  )

  let items = msgs.map((msg, i) => (
    <Segment key={i} attached>
      <Header dividing textAlign="center" sub size="huge">
        {msg.role || 'system'} message ({estimateTokens(msg.content)} tokens)
      </Header>
      {sysmsgMarkdown ? <Markdown>{escape(msg.content)}</Markdown> : <PreText text={msg.content} />}
    </Segment>
  ))

  let num_tokens = useMemo(() => {
    return sum(msgs.map(msg => estimateTokens(msg.content || '')))
  }, [msgs])

  let used = (num_tokens / model.maxInputTokens) * 100
  let cornerContent = (
    <span
      title={`${num_tokens} of ${model.maxInputTokens}`}
      style={{color: used > 80 ? 'red' : undefined}}
    >
      {used.toFixed(0)}%
    </span>
  )

  return (
    <XMenu.DialogItem
      content="Regeln"
      title="System messages used during last completion"
      cornerContent={cornerContent}
      dlgHeader={menu}
      name="sysmsgs"
    >
      <div style={{height: '100%', overflow: 'auto'}}>{items}</div>
    </XMenu.DialogItem>
  )
}

const History = ({historyctrl}) => {
  let {history, loadThreadFromHistory, setHistory, clearHistory, pinThreadInHistory} = historyctrl
  let entries = reversed(sorted(history, h => h.dt)).map(({thread_id, dt, messages, pinned}) => (
    <List.Item key={thread_id}>
      <List.Content floated="right">
        <Button
          size="tiny"
          compact
          basic
          icon="trash"
          onClick={() => setHistory(history.filter(h => h.thread_id !== thread_id))}
        />
        <Button
          size="tiny"
          compact
          basic
          icon="pin"
          active={pinned}
          onClick={() => pinThreadInHistory(thread_id, !pinned)}
        />
      </List.Content>
      <List.Header as="a" onClick={() => loadThreadFromHistory(thread_id)}>
        {messages?.[0].content}
      </List.Header>
      <List.Description>{dt}</List.Description>
    </List.Item>
  ))
  return (
    <div style={{minWidth: '200px', maxHeight: '70vh', overflow: 'auto'}}>
      <List divided relaxed>
        {entries}
      </List>
      <Button basic compact floated="right" onClick={() => clearHistory()} content="clear" />
    </div>
  )
}

const ShowFunctions = ({functions}) => {
  let funcs = functions
    ?.map(f => {
      let args = mapItems(
        f.parameters.properties,
        (param_name, {type, description}) => `## ${param_name} (${type})\n${description}\n`
      ).join('\n')
      return `# ${f.name}\n${f.description}\n${args}\n`
    })
    .join('\n\n')
  //~ return <pre>{funcs}</pre>
  return <Markdown>{funcs || '...'}</Markdown>
}

const HighlightedCode = ({className, onCodeClick, children}) => {
  let onClick = () => {
    callIfFunc(onCodeClick, children)
  }
  let cursorStyle = {cursor: onCodeClick ? 'pointer' : undefined}
  if (className?.startsWith('lang-')) {
    return (
      <Highlight code={children} language={className.slice(5)}>
        {({style, tokens, getLineProps, getTokenProps}) => (
          <code style={{...style, ...cursorStyle}} className={styles.highlight} onClick={onClick}>
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({line})}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({token})} />
                ))}
              </div>
            ))}
          </code>
        )}
      </Highlight>
    )
  }
  return (
    <code onClick={onClick} style={cursorStyle}>
      {children}
    </code>
  )
}
const MessageInteraction = ({messages, index, interact, hovered, onSetMessageEdit}) => {
  let res = null
  if (messages[index]['role'] == 'user') {
    res = (
      <>
        <Button
          icon="refresh"
          size="tiny"
          compact
          secondary
          floated="right"
          onClick={() => interact.retryFromUserMessage(index)}
          style={{opacity: hovered ? undefined : 0.1, transition: 'all 1s'}}
          title="retry"
        />
        <Button
          icon="edit"
          size="tiny"
          compact
          secondary
          floated="right"
          onClick={() => onSetMessageEdit(index)}
          style={{opacity: hovered ? undefined : 0.1, transition: 'all 1s'}}
          title="retry"
        />
      </>
    )
  }
  return res
}
const Chat = ({
  messages,
  contextRules,
  onEdited,
  onFunctionCallClick,
  interact,
  editLastMessage,
  onMarkdownLinkClick,
  pauseOnMarkdownLink,
  debug,
  wordsPerSec,
}) => {
  // track hovered message index
  let [hovered, setHovered] = useState(null)
  let [messageEdit, setMessageEdit] = useState(null)

  // go into editing mode changed externally
  useEffect(() => {
    if (editLastMessage !== null) {
      // last is llm output, so edit the user message befor
      setMessageEdit(messages.length - 2)
    }
  }, [editLastMessage])

  let icon = msg => {
    let name = 'server'
    if (msg.role == 'user') name = 'user'
    // else role assistant

    if (msg.meta?.function_calls) {
      // custom case: recovered function_calls by scanning text
      // here we have a combination of a natural language response plus parsed function calls
      return (
        <Icon.Group className={styles.iconGroup}>
          <Icon name={name} />
          <Icon name="cog" corner="bottom right" />
        </Icon.Group>
      )
    } else {
      return <Icon name={name} className={styles.singleIcon} />
    }
  }

  let renderedMessageOutputs = useMemo(
    () =>
      messages.map(msg => (
        <MessageOutput
          msg={msg}
          onFunctionCallClick={onFunctionCallClick}
          onMarkdownLinkClick={onMarkdownLinkClick}
          pauseOnMarkdownLink={pauseOnMarkdownLink}
          debug={debug}
          wordsPerSec={wordsPerSec}
        />
      )),
    [onMarkdownLinkClick, debug, onFunctionCallClick, messages, wordsPerSec]
  )

  let renderMessage = (msg, i, isLast) => {
    if (!msg.content && isLast && msg.meta?.streaming) {
      return (
        <DelayedDisplay delay={2000}>
          <Loader inline active indeterminate size="mini" style={{marginTop: '-5px'}} />
        </DelayedDisplay>
      )
    }

    // default is markdown output
    let res = renderedMessageOutputs[i]

    // customize tool/function call
    //~ let isFuncCall = msg =>
    //~ ['function_call'].includes(msg.finish_reason) ||
    //~ (msg.content.startsWith(msg.meta?.function_calls?.[0]?.name) && msg.content.endsWith('}'))
    //~ let format_args = args => mapItems(args, (k, v) => `${k}='${v}'`).join(', ')
    //~ if (isFuncCall(msg)) {
    //~ if (msg.meta.function_calls.length > 0) {
    //~ let multiple = msg.meta.function_calls.length > 1
    //~ let s = msg.meta.function_calls.map((fc, i) => (
    //~ <code key={i} style={{display: multiple ? 'inline-block' : undefined}}>{`${
    //~ fc.name
    //~ }(${format_args(fc.arguments)})`}</code>
    //~ ))

    //~ res = s
    //~ }
    //~ } else

    if (['remote_error', 'exception', 'bad_response'].includes(msg.finish_reason)) {
      // XXX: where is msg.content?
      res = (
        <code style={{color: '#C22'}}>
          {msg.finish_reason}: {msg.content}
        </code>
      )
    }
    return res
  }
  // context rules can indicate loaded stuff in chat window
  let indicators = mapItems(
    contextRules,
    (name, r) => {
      let ind = r.buildChatIndicator()
      if (ind) {
        return <span key={name}>{r.buildChatIndicator()}</span>
      }
      return null
    },
    (n, r) => r.buildChatIndicator
  )

  return (
    <div className={styles.container}>
      {messages.map((msg, index) => {
        return (
          <div
            key={index}
            style={{marginTop: '5px', marginBottom: '5px'}}
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
          >
            <MessageInteraction
              hovered={hovered == index}
              messages={messages}
              index={index}
              interact={interact}
              onSetMessageEdit={setMessageEdit}
            />
            {icon(msg)}
            {messageEdit == index ? (
              <MessageEditor
                msg={msg}
                index={index}
                onClose={() => setMessageEdit(null)}
                onSave={interact.editMessage}
              />
            ) : (
              <div className={styles.output}>
                {renderMessage(msg, index, index == messages.length - 1)}
              </div>
            )}
          </div>
        )
      })}
      {messages.at(-1)?.meta?.streaming ? null : indicators}
    </div>
  )
}

const ChatScroller = ({messages, ...props}) => {
  const containerRef = useRef(null)
  const isProgrammaticScroll = useRef(false)
  const [isAutoScrollActive, setIsAutoScrollActive] = useState(true)

  const onUserScroll = () => {
    if (isProgrammaticScroll.current) {
      isProgrammaticScroll.current = false
      return
    }
    const container = containerRef.current
    // Check if the scroll is at the bottom
    const isAtBottom =
      Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 20
    setIsAutoScrollActive(isAtBottom)
  }

  useEffect(() => {
    const container = containerRef.current
    if (container && isAutoScrollActive) {
      isProgrammaticScroll.current = true
      container.scrollTop = container.scrollHeight
    }
  }, [messages, isAutoScrollActive])

  return (
    <div
      ref={containerRef}
      style={{flex: 1, overflow: 'auto', marginBottom: '10px'}}
      onScroll={onUserScroll}
    >
      <Chat messages={messages} {...props} />
    </div>
  )
}

const PromptInput = ({onSetPrompt, onEditLastMessage, refocusOnPromptInput}) => {
  const ref = useRef(null)
  const [inputValue, setInputValue] = useState('')

  const handleInputChange = e => {
    setInputValue(e.target.value)
    resizeTextarea()
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault() // Prevent the default action to avoid a line break in the textarea
      if (strip(inputValue)) {
        onSetPrompt(inputValue) // Call the passed `onSetPrompt` function with the current input value
        setInputValue('') // Optionally clear the input after sending
      }
    }
    // if cursor up pressed and no text available setEditLastMessage to true
    if (e.key === 'ArrowUp' && strip(inputValue) === '') {
      // set a random value to wake up via useEffect
      onEditLastMessage(Math.random())
    }
  }

  const resizeTextarea = () => {
    let textarea = ref.current
    if (textarea) {
      textarea.style.height = 'auto' // Reset height to recalculating
      textarea.style.height = `${textarea.scrollHeight}px` // Set to scroll height
    }
  }
  useEffect(() => {
    if (ref.current) {
      ref.current.focus()
    }
  }, [refocusOnPromptInput])

  return (
    <textarea
      ref={ref}
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder="Type your message and hit Enter to send..."
      style={{
        maxHeight: '300px',
        height: 'auto',
        overflowY: 'hidden',
        flex: 1,
        padding: '10px',
        fontFamily: 'Arial, sans-serif',
        border: '1px solid #ccc',
        borderRadius: '5px',
        backgroundColor: '#f9f9f9',
        outline: 'none',
        transition: 'border-color 0.3s',
        resize: 'none',
      }}
      onFocus={() => (ref.current.style.borderColor = '#80bdff')}
      onBlur={() => (ref.current.style.borderColor = '#ccc')}
    />
  )
}

export const ChatPanel = ({
  contextRules = {}, // complex client + python side system message building
  systemMessages, // one ore more raw system messages
  systemMessageEditor,
  functions,
  onFunctionCall, // react on function calls that are emitted due to python side context building (or raw function defs)
  onMarkdownLinkClick,
  pauseOnMarkdownLink = false,
}) => {
  let [model, modelSelector] = useModelSelector({})
  let {menuItem: contextToggle, toggleState} = useContextToggles(contextRules)
  let [temperature, setTemperature] = usePersistent('temperature', 1, 4)
  let [wordsPerSec, setWordsPerSec] = usePersistent('wordsPerSec', 0, 1)
  let [debug, debug_toggle] = usePersistentToggle('debug_chat', {
    icon: 'bug',
  })

  let _onFunctionCall = useCallback(func_call => callIfFunc(onFunctionCall, func_call), [
    onFunctionCall,
  ])
  // build list of context names from toggle states
  let activeContextRules = useMemo(
    () => dict(mapItems(contextRules, (name, r) => [name, r], name => toggleState[name])),
    [toggleState, contextRules]
  )
  let context = keys(activeContextRules)
  let [refocusOnPromptInput, setRefocusOnPromptInput] = useState(0)

  let {
    messages,
    reset,
    newPrompt,
    loading,
    rate_limits,
    debug_data,
    historyctrl,
    interact,
  } = usePersistentChat({
    systemMessages,
    context,
    model: model.name,
    temperature: (temperature / 100).toFixed(3),
    onFunctionCall: _onFunctionCall,
    contextRules: activeContextRules,
    setRefocusOnPromptInput,
  })

  useEffect(() => {
    if (debug) vl.pprint(debug_data)
  }, [debug_data, debug])

  let total_price = sum(messages.filter(m => m.meta?.price).map(m => m.meta.price))
  let total_in_toks = Math.floor(
    sum(messages.filter(m => m.meta?.num_input_tokens).map(m => m.meta.num_input_tokens))
  )

  let last_in_toks = Math.floor(messages.at(-1)?.meta?.num_input_tokens)
  // dispatch edit command between PromptInput and ChatScroller
  let [editLastMessage, setEditLastMessage] = useResettableState(null, messages)

  return (
    <AutoSizer>
      {({height, width}) => (
        <div style={{height, width, display: 'flex', flexDirection: 'column'}}>
          <XMenu attached size="small">
            <XMenu.Item icon="trash" onClick={reset} />
            {contextToggle}
            {modelSelector}
            <XMenu.BeginRight />

            <XMenu.Label
              content={`${total_price.toFixed(1)}c`}
              title={`sum input tokens: ${total_in_toks} last: ${last_in_toks}`}
            />

            <SystemMessageItem
              returnedSystemMessages={debug_data.sys_messages || []}
              rawSystemMessages={systemMessages}
              systemMessageEditor={systemMessageEditor}
              model={model}
            />

            {size(debug_data.functions) > 0 && (
              <XMenu.PopupItem
                content="F"
                padded
                keepOpen
                title="Functions declared during last completion"
              >
                <div className={styles.functions_markdown}>
                  <ShowFunctions functions={debug_data.functions} />
                </div>
              </XMenu.PopupItem>
            )}
            <XMenu.PopupItem content="RL" padded keepOpen title="Rate Limits">
              {mapItems(rate_limits, (name, value) => (
                <div key={name}>
                  {name}: {value}
                </div>
              ))}
            </XMenu.PopupItem>
            <XMenu.PopupItem
              icon="calendar"
              wide="very"
              padded
              disabled={historyctrl.history.length == 0}
              open={historyctrl.history.length == 0 ? false : undefined}
            >
              <History historyctrl={historyctrl} />
            </XMenu.PopupItem>
            {debug_toggle}
            <XMenu.SubMenu icon="configure" keepOpen>
              <XMenu.Label content="Temperature" />
              <XMenu.Slider
                value={temperature}
                min={0}
                max={100}
                onValueChange={setTemperature}
                title="Temperature (response randomnes) (0 = auto)"
                valueLabeled
              />
              <XMenu.Label content="Words/Sec" />
              <XMenu.Slider
                value={wordsPerSec}
                min={0}
                max={30}
                onValueChange={setWordsPerSec}
                title="Speed of drawing new words (0 = disabled limit)"
                valueLabeled
              />
            </XMenu.SubMenu>
            <XMenu.Item
              icon="help"
              title="Help me"
              onClick={() =>
                newPrompt('Beschreibe deine Aufgabe und Funktion und zeige Beispiele.')
              }
            />
            <XMenu.Item
              content={LUCKY}
              title="I'm feeling lucky!"
              onClick={() => newPrompt(LUCKY)}
            />
          </XMenu>
          <ChatScroller
            messages={messages}
            onFunctionCallClick={_onFunctionCall}
            contextRules={activeContextRules}
            interact={interact}
            editLastMessage={editLastMessage}
            onMarkdownLinkClick={onMarkdownLinkClick}
            pauseOnMarkdownLink={pauseOnMarkdownLink}
            debug={debug}
            wordsPerSec={wordsPerSec}
          />
          <div style={{display: 'flex'}}>
            <PromptInput
              onSetPrompt={newPrompt}
              onEditLastMessage={setEditLastMessage}
              refocusOnPromptInput={refocusOnPromptInput}
            />
          </div>
        </div>
      )}
    </AutoSizer>
  )
}
